import { BookingMinimal } from '../booking';
import { CarMinimalInfoV2, CarWheelSetType } from '../cars';
import { Currency } from '../currency';
import { SMS } from '../interactions';
import { InventoryOrder } from '../inventoryOrder';
import { Language } from '../language';
import { Money } from '../salesItem';

import { TireFromInventorySupplier } from '../tireFromInventory';
import { UserMinimalInfo } from '../user';
import { UserGroup } from '../userGroup';

export const paymentMethod = {
  INVOICE: 'INVOICE',
  PAY_BEFORE: 'PAY_BEFORE'
} as const;

export type PaymentMethod = (typeof paymentMethod)[keyof typeof paymentMethod];

export const paymentMethodToLabel = {
  INVOICE: 'Invoice',
  PAY_BEFORE: 'Pay in advance'
} as const;

export const paymentMethodOptions = Object.values(paymentMethod).map((method) => ({
  label: paymentMethodToLabel[method],
  value: method
}));

export interface TireOfferV2 {
  car: {
    make: string;
    model: string;
    licensePlate: { number: string; countryCode: string };
    id: number;
  };
  createdAt: string;
  createdBy: number;
  genericItems: {
    name: string;
    id: number;
    price: Money;
    description: string;
  }[];
  id: number;
  isPending: boolean;
  isRejected: boolean;
  isSelected: boolean;
  isStaggered: boolean;
  quoteItems: TireQuoteItem[];
  rejectedAt: string;
  selectedAt: string;
  slug: string;
  updatedAt: string;
  updatedBy: number;
  userGroup: UserGroup;
  views: TireOfferView[];
  status: QuoteItemStatus;
  season: CarWheelSetType;
  paymentMethod: PaymentMethod;
  smses: { sms: SMS; createdAt: string; createdBy: UserMinimalInfo }[];
  orderShop?: OrderShop;
}

export interface TireOfferMinimal {
  car: CarMinimalInfoV2;
  createdAt: string;
  createdBy: number;
  id: number;
  isPending: boolean;
  isRejected: boolean;
  isSelected: boolean;
  isStaggered: boolean;
  rejectedAt: string;
  selectedAt: string;
  slug: string;
  updatedAt: string;
  updatedBy: number;
  userGroup: UserGroup;
  status: QuoteItemStatus;
  season: CarWheelSetType;
  upcomingBooking: BookingMinimal;
  hasWheelSetInStorageFacility: boolean;
  quantitySelected?: number;
  paymentMethod: PaymentMethod;
  orderShop?: OrderShop;
}

export const staggeredPositions = ['FRONT', 'REAR'] as const;
interface OrderShop {
  id: number;
  slug: string;
}

export type StaggeredPosition = (typeof staggeredPositions)[number];
export interface TireInQuoteItem {
  tireInventory: TireFromInventorySupplier;
  quantityDefault: number;
  quantityMax: number;
  quantityMin: number;
  priceAdHoc?: Money;
  quantitySelected?: number;
  staggeredPosition?: { value: StaggeredPosition; label: string };
  id: number;
  inventoryOrder?: InventoryOrder;
}

export interface TireQuoteItem {
  model: string;
  description: string;
  isRecommended: boolean;
  quoteItemTires: TireInQuoteItem[];
  id: number;
  isSelected: boolean;
}

export interface TireQuoteSalesItem {
  id: number;
  currency: Currency;
  description: string;
  name: string;
  price: number;
  shortDescription: string;
}

interface ViewedByUser {
  email: string;
  id: number;
  language: Language;
  name: string;
  phoneNumber: string;
}

interface TireOfferView {
  viewedAt: string;
  viewedBy?: ViewedByUser;
}

export type TireOfferSeason = 'SUMMER' | 'WINTER';
export interface TireQuotePayload {
  carId: number;
  sendSms?: boolean;
  season: TireOfferSeason;
  genericItemsIds: number[];
  serviceDepartmentId: number;
  paymentMethod: PaymentMethod;
  quoteItems: {
    isRecommended: boolean;
    quoteItemTires: {
      tireInventoryId: number;
      quantityDefault: number;
      quantityMin: number;
      quantityMax: number;
      quantitySelected: number;
    }[];
  }[];
}

export interface CarTireQuoteFilters {
  carIds?: number[];
  season?: TireOfferSeason;
  genericItemsIds?: number[];
  userGroupIds?: number[];
  isStaggered?: boolean | null;
  isSelected?: boolean | null;
  isRejected?: boolean | null;
  status?: QuoteItemStatusType | null;
  statuses?: QuoteItemStatusType[] | null;
}

export const quoteItemStatus = {
  CREATED: 'CREATED',
  PENDING: 'PENDING',
  REJECTED: 'REJECTED',
  WITHDRAWN: 'WITHDRAWN',
  ACCEPTED_WAITING_PAYMENT: 'ACCEPTED_WAITING_PAYMENT',
  ACCEPTED_WITH_PAYMENT: 'ACCEPTED_WITH_PAYMENT',
  INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL: 'INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL',
  INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL: 'INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL',
  INVENTORY_RECEIVED_IN_FULL: 'INVENTORY_RECEIVED_IN_FULL',
  INVENTORY_RECEIVED_PARTIAL: 'INVENTORY_RECEIVED_PARTIAL',
  TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY: 'TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY',
  TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER: 'TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER',
  FULFILLED_WITHOUT_PAYMENT: 'FULFILLED_WITHOUT_PAYMENT',
  FULFILLED_WITH_PAYMENT: 'FULFILLED_WITH_PAYMENT'
} as const;

export const quoteItemStatusToLabel = {
  CREATED: 'Created',
  PENDING: 'Pending',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
  ACCEPTED_WAITING_PAYMENT: 'Accepted waiting payment',
  ACCEPTED_WITH_PAYMENT: 'Accepted with payment',
  INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL: 'Inventory ordered at suppliers in full',
  INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL: 'Inventory ordered at suppliers in partial',
  INVENTORY_RECEIVED_IN_FULL: 'Inventory received in full',
  INVENTORY_RECEIVED_PARTIAL: 'Inventory received in partial',
  TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY: 'Tires are mounted on wheels at storage facility',
  TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER: 'Tires are mounted on wheels at customer',
  FULFILLED_WITHOUT_PAYMENT: 'Fulfilled without payment',
  FULFILLED_WITH_PAYMENT: 'Fulfilled with payment'
} as const;

export const quoteItemStatusesOptions = Object.values(quoteItemStatus).map((status) => ({
  label: quoteItemStatusToLabel[status],
  value: status
}));

export type QuoteItemStatusType = (typeof quoteItemStatus)[keyof typeof quoteItemStatus];

export interface QuoteItemStatus {
  createdAt: string;
  createdBy: UserMinimalInfo;
  id: number;
  quoteId: number;
  status: QuoteItemStatusType;
}

export interface QuoteStatusEvent {
  id: number;
  quoteId: number;
  status: QuoteItemStatusType;
  createdAt: string;
  createdBy: UserMinimalInfo;
}
